import React, { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import { ChangePasswordPage } from "./components/auth/ChangePasswordPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { Layout } from "./components/layout/Layout";
import {
  getEnvironmentInfo,
  initEnvironmentInfo,
  type EnvironmentInfo,
} from "./config/EnvironmentConfig";
import { AuthProvider } from "./contexts/AuthContext";
import { ContactPage as AdminContactPage } from "./pages/entities/contact/ContactPage";
import { MaintenanceGuard } from "./public/components/MaintenanceGuard";
import {
  HomePage,
  AboutPage,
  ServicesPage,
  WorksPage,
  WorksDetailPage,
  RecruitPage,
  ContactPage,
  PrivacyPolicyPage,
  NewsPage,
  NewsDetailPage,
  NotFoundPage,
} from "./public/pages";
import { getAllEntityRoutes } from "./types/generated/EntityRoutes";
import { ScrollToTop } from "./components/ScrollToTop";
import { AnalyticsTracker } from "./public/analytics/AnalyticsTracker";
import { AnalyticsPage } from "./pages/analytics/AnalyticsPage";


function App() {
  const [environmentInfo, setEnvironmentInfo] =
    useState<EnvironmentInfo | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // 完全な環境情報（シークレット情報含む）を取得
      await initEnvironmentInfo();
      if (!mounted) return;
      const envInfo = getEnvironmentInfo();
      setEnvironmentInfo(envInfo);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // 完全な環境情報が読み込まれるまで待機
  if (!environmentInfo) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
          gap: "16px",
        }}
      >
        <div>環境情報を読み込み中...</div>
        <div style={{ fontSize: "14px", color: "#999" }}>
          シークレット情報を取得しています
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <ScrollToTop />
      <AnalyticsTracker />
      <Routes>
          {/* ========================================
              一般公開ページ（ログイン不要）
              ======================================== */}
          <Route path="/" element={<MaintenanceGuard><HomePage /></MaintenanceGuard>} />
          <Route path="/about" element={<MaintenanceGuard><AboutPage /></MaintenanceGuard>} />
          <Route path="/services" element={<MaintenanceGuard><ServicesPage /></MaintenanceGuard>} />
          <Route path="/works" element={<MaintenanceGuard><WorksPage /></MaintenanceGuard>} />
          <Route path="/works/:id" element={<MaintenanceGuard><WorksDetailPage /></MaintenanceGuard>} />
          <Route path="/recruit" element={<MaintenanceGuard><RecruitPage /></MaintenanceGuard>} />
          <Route path="/contact" element={<MaintenanceGuard><ContactPage /></MaintenanceGuard>} />
          <Route path="/privacy-policy" element={<MaintenanceGuard><PrivacyPolicyPage /></MaintenanceGuard>} />
          <Route path="/news" element={<MaintenanceGuard><NewsPage /></MaintenanceGuard>} />
          <Route path="/news/:id" element={<MaintenanceGuard><NewsDetailPage /></MaintenanceGuard>} />

          {/* ========================================
              Admin ページ（認証必要）
              ======================================== */}
          <Route
            path="/admin/*"
            element={
              <Layout
                environment={environmentInfo.type}
                version={environmentInfo.version}
              >
                <Routes>
                  {/* お問い合わせ（デフォルトページ） */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <AdminContactPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* アクセス解析（サーバー管理者のみ） */}
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRoute>
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* 自分のパスワード変更 */}
                  <Route
                    path="password"
                    element={
                      <ProtectedRoute>
                        <ChangePasswordPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* 自動生成されたエンティティルート */}
                  {getAllEntityRoutes().map((route, index) => (
                    <Route
                      key={index}
                      path={route.props.path}
                      element={
                        <ProtectedRoute>{route.props.element}</ProtectedRoute>
                      }
                    />
                  ))}

                  {/* Admin 404ページ */}
                  <Route
                    path="*"
                    element={
                      <div
                        style={{
                          textAlign: "center",
                          padding: "48px 24px",
                          color: "#6b7280",
                        }}
                      >
                        <h2>404 - ページが見つかりません</h2>
                        <p>お探しのページは存在しません。</p>
                      </div>
                    }
                  />
                </Routes>
              </Layout>
            }
          />

          {/* 公開側 404（nginx も未知の URL には 404 ステータスで SPA を返す） */}
          <Route path="*" element={<MaintenanceGuard><NotFoundPage /></MaintenanceGuard>} />
        </Routes>
    </AuthProvider>
  );
}

export default App;
